import type { tWalletCurrency, tWalletCurrencyOrEmpty } from './Wallet.types';
import { WALLET_CURRENCIES } from './Wallet.types';

/**
 * Money and date formatting for the wallet (RET-1684, contract closed 2026-08-05).
 *
 * WHY THIS FILE EXISTS AT ALL — it is the documented exception to the money
 * rule. The rule (SSOT §7) says the backend sends amounts already formatted and
 * the front never formats money, because under zero margin two formatters that
 * disagree are a visible leak. The wallet backend does NOT do that, on purpose:
 * the symbol, the separators and their order depend on the user's locale, which
 * lives in this repo's i18n and which the wallet service does not know. Sending
 * "US$8.15" from Go would hardcode one locale into a service that serves USD
 * and MXN today and whatever comes next.
 *
 * What the rule actually protected — that two roundings produce two numbers —
 * is kept by exactness instead of by formatting:
 *
 *   * The backend sends the EXACT integer in `amountMicros` (a string, because
 *     JSON numbers past 2^53 silently lose money) and a 6-decimal exact string
 *     in `amount`.
 *   * The front rounds ONCE, from that exact integer, never from an already
 *     rounded value.
 *   * The rounding contract is HALF-UP (away from zero) to 2 decimals:
 *     -8.155000 -> -8.16, not -8.15.
 *
 * All the arithmetic below is BigInt. Doing it in Number would reintroduce the
 * exact drift the micro-unit integer exists to prevent — (8.155).toFixed(2) is
 * already unreliable because 8.155 has no exact binary representation.
 */

/** 1 cent = 10,000 micro-units. Both currencies in play have 2 decimals. */
const MICROS_PER_CENT = 10_000n;
const CENTS_PER_UNIT = 100n;

/**
 * Formatting locale, derived from the UI language.
 *
 * This app runs on `lng: 'es'` (see i18n.ts), and bare "es" is PENINSULAR
 * Spanish to Intl: it formats US$2,847.60 as "2847,60 US$" — comma decimal,
 * symbol trailing. That is wrong for every market this product sells to, and
 * it is wrong in the most expensive place possible, a money figure.
 *
 * "es-MX" is used instead because Mexico is the dominant market and its
 * convention (point decimal, comma thousands) reads correctly across LatAm.
 * Any language that already carries a region is respected as-is.
 *
 * It also keeps USD and MXN distinguishable: es-MX renders MXN as "$" and USD
 * as "USD". `currencyDisplay: 'narrowSymbol'` would render BOTH as "$", which
 * on a product that bills in both is not a cosmetic choice — it is a customer
 * reading a dollar figure as pesos.
 *
 * "br" gets the same treatment for a nastier reason. It is this app's namespace
 * for Portuguese (see public/locales/br), but to Intl "br" is BRETON — a real
 * language it supports, so `supportedLocalesOf(['br'])` answers `['br']` and
 * nothing falls back. MXN 245 comes out "245,00 $" instead of pt-BR's
 * "$ 245,00". A wrong locale that throws is a bug you find; one that formats
 * money in a Celtic language of Brittany is a bug you ship.
 */
export function resolveMoneyLocale(language: string): string {
  if (!language) return 'es-MX';

  const normalized = language.toLowerCase();

  if (normalized === 'es') return 'es-MX';
  if (normalized === 'br') return 'pt-BR';

  return language;
}

/**
 * Rounds exact micro-units to cents, half-up (away from zero).
 *
 * Away from zero is what the contract says, and it is the reading that keeps a
 * refund and a charge of the same magnitude symmetric: -8.155 -> -8.16 mirrors
 * +8.155 -> +8.16. Rounding toward zero on one side only would make a
 * reversal fail to cancel its original row by one cent.
 */
export function roundMicrosToCents(amountMicros: string): bigint {
  const micros = BigInt(amountMicros);
  const isNegative = micros < 0n;
  const magnitude = isNegative ? -micros : micros;
  const cents = (magnitude + MICROS_PER_CENT / 2n) / MICROS_PER_CENT;

  return isNegative ? -cents : cents;
}

/**
 * Picks the currency to display in, given what the wallet answered and what
 * the subscription says.
 *
 * The wallet's own answer wins whenever it has one — it is the currency the
 * ledger is actually locked to. The subscription is the fallback for the empty
 * case, and it is the RIGHT fallback rather than a guess: it is the same source
 * the wallet's first movement would have stamped.
 *
 * Returns null when neither resolves. Callers must not substitute a default —
 * printing "US$0.00" for a workspace that bills in MXN is a wrong number, and
 * the whole point of the wallet is that displayed money is never wrong.
 */
export function resolveWalletCurrency(
  walletCurrency: tWalletCurrencyOrEmpty,
  subscriptionCurrency?: string | null,
): tWalletCurrency | null {
  if (walletCurrency) return walletCurrency;

  const fromSubscription = (subscriptionCurrency ?? '').toUpperCase();

  return WALLET_CURRENCIES.includes(fromSubscription as tWalletCurrency)
    ? (fromSubscription as tWalletCurrency)
    : null;
}

/**
 * Formats an exact micro-unit amount for display.
 *
 * `currency` may be empty: the backend derives it from the ledger's own first
 * movement, so a wallet that has never moved money has no derivable currency
 * (it answers `currency: ""` with `amountMicros: "0"`). Callers pass the
 * subscription's currency as the fallback — see `resolveWalletCurrency`.
 */
export function formatWalletAmount(options: {
  amountMicros: string;
  currency: tWalletCurrency;
  locale: string;
  /** Prefixes "+" on credits, matching the prototype's activity table. */
  showSign?: boolean;
  /**
   * Drops the decimals when the amount has NO cents — "$1,000" instead of
   * "$1,000.00".
   *
   * Only for the top-up amounts (presets and the modal's total), where every
   * figure is a closed amount the backend offers and the ".00" is noise the
   * customer has to read past on every option.
   *
   * It is conditional on the cents being zero rather than unconditional, and
   * that is the whole safety of it: an amount with real cents keeps them. A
   * flag that always trimmed would turn a hypothetical $1,000.50 preset into
   * "$1,000" — hiding money the customer is about to be charged, which is the
   * one thing this file exists to prevent. Balance and the activity table never
   * pass this: there the cents are real (consumption is billed per message) and
   * rounding them away would be a wrong number.
   */
  trimZeroCents?: boolean;
}): string {
  const cents = roundMicrosToCents(options.amountMicros);

  // Number is exact for integers below 2^53; past that the conversion would
  // start losing cents. No real balance gets there, but a corrupt payload
  // should fail loudly rather than print a quietly wrong number.
  if (
    cents > BigInt(Number.MAX_SAFE_INTEGER) ||
    cents < -BigInt(Number.MAX_SAFE_INTEGER)
  ) {
    throw new RangeError(
      `wallet amount out of displayable range: ${options.amountMicros}`,
    );
  }

  const units = Number(cents) / Number(CENTS_PER_UNIT);

  // Sólo se sueltan los decimales cuando de verdad no hay centavos que mostrar.
  // El resto del tiempo ambas cotas siguen fijas en 2 — ver abajo.
  const hasNoCents = cents % CENTS_PER_UNIT === 0n;
  const fractionDigits = options.trimZeroCents && hasNoCents ? 0 : 2;

  const formatted = walletAmountFormatter({
    currency: options.currency,
    locale: options.locale,
    fractionDigits,
    showSign: options.showSign,
  }).format(units);

  return `${formatted}${CURRENCY_SUFFIX_SEPARATOR}${options.currency}`;
}

/**
 * El separador entre la cifra y el código de la moneda ("$1,000 USD").
 *
 * Es un espacio DURO (U+00A0) para que "1,000" y "USD" no se partan en dos
 * renglones dentro de un botón estrecho, que es donde viven los presets. Intl
 * usa el mismo carácter por la misma razón; usar uno normal aquí haría que dos
 * espacios visualmente idénticos se comportaran distinto.
 */
const CURRENCY_SUFFIX_SEPARATOR = ' ';

/**
 * El formateador que TODO el wallet usa, en un solo lugar.
 *
 * `narrowSymbol` fuerza "$" también en dólares — es-MX rendería "USD 1,000" con
 * el default, y la cifra que el cliente lee no debería cambiar de forma según su
 * moneda. Lo que ese default protegía —que USD y MXN no se confundan— se
 * conserva por otra vía: el código va SIEMPRE pegado al importe como sufijo, así
 * que "$1,000 USD" y "$1,000 MXN" siguen siendo distinguibles de un vistazo. Es
 * la decisión del 18-ago-2026; la anterior era ilegible en los presets.
 *
 * NO es opcional ni configurable a propósito. Dos amounts formateados distinto
 * en la misma pantalla es exactamente cómo alguien lee dólares como pesos, y
 * este archivo existe para que el dinero mostrado nunca esté mal.
 */
function walletAmountFormatter(options: {
  currency: tWalletCurrency;
  locale: string;
  fractionDigits: number;
  showSign?: boolean;
}): Intl.NumberFormat {
  return new Intl.NumberFormat(resolveMoneyLocale(options.locale), {
    style: 'currency',
    currency: options.currency,
    currencyDisplay: 'narrowSymbol',
    // Both bounds pinned to the SAME value: the amount is already rounded to
    // the contract, so Intl must render it, not round it again. Pinning them
    // together is what keeps that true whether the value shows cents or not.
    minimumFractionDigits: options.fractionDigits,
    maximumFractionDigits: options.fractionDigits,
    signDisplay: options.showSign ? 'exceptZero' : 'auto',
  });
}

/**
 * Lo que va ANTES y DESPUÉS de la cifra, para el campo donde el cliente teclea
 * su propio monto.
 *
 * Ese campo no puede pasar por `formatWalletAmount` —recibe micro-unidades
 * exactas, y lo que hay en el input es una cadena a medio escribir que
 * reformatear pelearía con el cursor— pero SÍ tiene que verse igual que los
 * presets de arriba. Los afijos se derivan del mismo formateador en vez de
 * escribirse a mano, así que no pueden divergir el día que cambie el formato.
 *
 * Se usa `formatToParts` y no un recorte por regex: Intl decide qué partes son
 * número y cuáles no, y preguntárselo es exacto. Recortar dígitos del final
 * dejaba de funcionar en cuanto apareció el sufijo de la moneda.
 */
export function walletAmountAffixes(options: {
  currency: tWalletCurrency;
  locale: string;
}): { prefix: string; suffix: string } {
  const parts = walletAmountFormatter({
    currency: options.currency,
    locale: options.locale,
    fractionDigits: 0,
  }).formatToParts(0);

  const NUMERIC = new Set(['integer', 'group', 'decimal', 'fraction']);
  let prefix = '';
  let suffix = '';
  let seenNumber = false;

  for (const part of parts) {
    if (NUMERIC.has(part.type)) {
      seenNumber = true;
      continue;
    }
    if (seenNumber) suffix += part.value;
    else prefix += part.value;
  }

  return {
    prefix,
    suffix: `${suffix}${CURRENCY_SUFFIX_SEPARATOR}${options.currency}`,
  };
}

/**
 * Formats the civil date of a movement row.
 *
 * `date` is a civil date (YYYY-MM-DD) with no time and no zone: the backend
 * already decided which day a movement belongs to, in a single timezone, so
 * that the day cannot move under the customer's feet.
 *
 * It is parsed into local date PARTS rather than through `new Date(iso)`, which
 * reads a bare YYYY-MM-DD as UTC midnight — in any timezone west of Greenwich
 * that formats as the previous day. Re-interpreting the day here is precisely
 * the bug the backend's fixed timezone exists to prevent, and it would show up
 * as an off-by-one-day only for some users, which is the worst way to find it.
 */
export function formatMovementDate(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);

  if (!year || !month || !day) return isoDate;

  return new Intl.DateTimeFormat(resolveMoneyLocale(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

/**
 * Formats an RFC3339 instant as a date ("1 de septiembre de 2026", or
 * "1 sept 2026" with `monthStyle: 'short'`).
 *
 * Used for the recurring top-up's first and next charge, which the backend sends
 * as a timestamp rather than as text (RET-1690). It is deliberately NOT
 * `formatMovementDate`: that one takes a civil YYYY-MM-DD that the backend
 * already bucketed into a day, and parsing an instant with it would drop the
 * zone and shift the day.
 *
 * It is rendered in UTC, NOT in the viewer's zone, and that is the whole point.
 * What the backend sends is a billing DAY expressed as an instant — the day
 * Stripe runs the charge and the day the invoice will show. Converting it to
 * local time shifts it for everyone west of Greenwich: a UTC-midnight instant
 * renders as the PREVIOUS day in every American timezone, so the modal would
 * promise "31 de agosto" for a charge the invoice dates "1 de septiembre".
 *
 * It is the same off-by-one that RET-1785 fixed for the activity table, and it
 * has the same tell: it only breaks for some users, which is the worst way to
 * find it. A test caught it here precisely because it stopped depending on the
 * runner's timezone.
 *
 * Returns an empty string for an absent or unparseable value rather than
 * throwing or printing "Invalid Date": the caller hides the line instead of
 * promising a date nobody can honour.
 *
 * `monthStyle` only picks how long the month reads; everything above — the UTC
 * rendering, the empty string on an absent value — holds either way. The short
 * form exists for the balance card's auto top-up subtitle, where the date is
 * the tail of a line that already carries an amount and a cadence, and a long
 * month pushes it onto a second row. It matches the activity table's shape
 * ("17 oct 2025"), which is the other short date on the same screen.
 */
export function formatWalletDate(
  rfc3339: string | undefined,
  locale: string,
  monthStyle: 'long' | 'short' = 'long',
): string {
  if (!rfc3339) return '';

  const parsed = new Date(rfc3339);

  if (Number.isNaN(parsed.getTime())) return '';

  return new Intl.DateTimeFormat(resolveMoneyLocale(locale), {
    day: 'numeric',
    month: monthStyle,
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

/**
 * Formats an RFC3339 instant as a day and a 24-hour time ("21 oct 11:04")
 * in the VIEWER's zone (RET-2426: «En pausa desde …»).
 *
 * It is the counterpart of `formatWalletDate`, and the difference is on
 * purpose. That one prints a billing DAY, which is the same day for everyone
 * and so is read in UTC. This one prints the moment a charge failed, which is
 * an event the customer lived in their own clock: «desde 11:04» only means
 * something if 11:04 is the hour on their wall.
 *
 * The year is left out: a pause that is a year old is not a pause, and the
 * subtitle is a short line. Empty string for an absent or unparseable value,
 * so the caller can drop the clause instead of printing "Invalid Date".
 */
export function formatWalletDateTime(
  rfc3339: string | undefined,
  locale: string,
): string {
  if (!rfc3339) return '';

  const parsed = new Date(rfc3339);

  if (Number.isNaN(parsed.getTime())) return '';

  // Day and time are formatted apart and joined with a space: the DS voice
  // guide asks for «DD mes HH:MM» in 24 h («21 oct 09:04»), and a single
  // formatter prints «21 oct, 9:04 a.m.» in es-MX.
  const resolved = resolveMoneyLocale(locale);
  const day = new Intl.DateTimeFormat(resolved, {
    day: 'numeric',
    month: 'short',
  }).format(parsed);
  const time = new Intl.DateTimeFormat(resolved, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(parsed);

  return `${day} ${time}`;
}

/**
 * Converts a typed amount to exact micro-units, or null when it is not a clean
 * decimal.
 *
 * String arithmetic on purpose: `Number('8.15') * 1_000_000` is 8149999.999… in
 * binary floating point, which is the exact drift the micro-unit integer exists
 * to prevent. The fraction is padded and truncated to 6 places, the precision
 * the whole contract is built on.
 */
export function customAmountToMicros(raw: string): string | null {
  const trimmed = raw.trim().replace(',', '.');

  if (!/^\d+(\.\d{1,6})?$/.test(trimmed)) return null;

  const [whole, fraction = ''] = trimmed.split('.');
  const micros = BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(6, '0'));

  return micros > 0n ? micros.toString() : null;
}

/** One whole unit ($1) in micro-units. Both wallet currencies use 6 decimals. */
const MICROS_PER_UNIT = 1_000_000n;

/**
 * Ceils exact micro-units up to the next WHOLE unit (RET-1806, RC-10: the
 * top-up's free amount is entero, sin centavos).
 *
 * `12_300_000` micros ($12.30) → `13`, never `12` (a floor would leave the
 * top-up short of covering the send) and never `12.3` (RC-10 forbids cents on
 * the free amount). BigInt-only: this is exactly the shortfall-to-prefill
 * step that must survive amounts past 2^53 — see `computeTopUpPrefillAmount`.
 */
export function ceilMicrosToWholeUnits(micros: bigint): bigint {
  if (micros <= 0n) return 0n;

  return (micros + MICROS_PER_UNIT - 1n) / MICROS_PER_UNIT;
}

/**
 * The top-up's free-amount prefill that covers a send (RET-1806, DS-03 +
 * DS-04 + RC-10).
 *
 * `prefill = max(ceil_to_whole_unit(shortfallMicros), ceil_to_whole_unit(minFreeAmountMicros))`
 *
 * where `shortfallMicros` is the exact amount missing to cover the send
 * (`|balanceAfter|` when negative) and `minFreeAmountMicros` is RC-10's floor
 * for the free amount, AS IT ARRIVES IN THE PAYLOAD
 * (`options.minFreeAmountMicros`) — never hardcoded here (RET-1895, explicit
 * PM correction). Absent, empty or malformed min = NO client-side floor: the
 * result is just the ceiled shortfall and the server is left to reject it,
 * same criterion `TopUpModal.connected.tsx`'s own `customAmountError` already
 * applies to this exact field.
 *
 * Returns the WHOLE-UNITS string the free-amount field expects ("13", never
 * "13.00" or "13000000") — `MoneyInput` renders it with `maxDecimals={0}`.
 *
 * BigInt end to end: `shortfallMicros` routinely exceeds 2^53 once expressed
 * in micros, and `Number(...) / 1e6` would silently round to the WRONG whole
 * unit past that threshold — a dollar short on a top-up meant to "cover the
 * send".
 */
export function computeTopUpPrefillAmount(
  shortfallMicros: string,
  minFreeAmountMicros?: string,
): string {
  const shortfall = /^\d+$/.test(shortfallMicros)
    ? BigInt(shortfallMicros)
    : 0n;
  const ceiledShortfall = ceilMicrosToWholeUnits(shortfall);

  const minWholeUnits =
    minFreeAmountMicros && /^\d+$/.test(minFreeAmountMicros)
      ? ceilMicrosToWholeUnits(BigInt(minFreeAmountMicros))
      : 0n;

  const prefill =
    ceiledShortfall > minWholeUnits ? ceiledShortfall : minWholeUnits;

  return prefill.toString();
}

/**
 * Converts exact micro-units to the 6-decimal string wallet-service's
 * `domain.Parse` accepts on the wire.
 *
 * This is the inverse of `customAmountToMicros`, and it exists because micros
 * is the front's INTERNAL format (presets arrive and are compared in micros —
 * see TopUpModal's preset-preselection logic) but wallet-service's charge
 * endpoint expects a DECIMAL string, not micros. Sending micros verbatim is
 * exactly the bug this function fixes: MXN $500 ("500000000" micros) went
 * out as `amount: "500000000"`, which `domain.Parse` read as 500 million,
 * blowing past the MXN 200000 maximum and 400ing every top-up.
 *
 * BigInt arithmetic on purpose, same reasoning as `customAmountToMicros`:
 * amounts routinely exceed 2^53 once expressed in micros (MXN 200000 is
 * 200_000_000_000 micros), and `Number(micros) / 1e6` would silently drift
 * once past that threshold — real money lost in a float, not a crash. String
 * splitting on whole/fraction avoids that entirely: no division, no float,
 * ever touches the value.
 */
export function microsToDecimal(amountMicros: string): string {
  // El guard existe porque esta funcion es un export publico y BigInt() lanza un
  // SyntaxError CRUDO ante cualquier cosa que no sea un entero. Hoy el unico
  // camino que llega aqui esta validado aguas arriba, pero un SyntaxError sin
  // contexto disparado dentro del try de chargeTopUp se reporta a Sentry como un
  // fallo de infraestructura del cobro, no como el input invalido que es. Se
  // rechaza con un mensaje que nombra la funcion y el valor.
  //
  // No se usa WalletContractError a proposito: vive en Wallet.service.ts, que ya
  // importa este modulo, y traerlo de vuelta cerraria el ciclo.
  if (!/^-?\d+$/.test(amountMicros)) {
    throw new Error(
      `microsToDecimal: se esperaba un entero en micro-unidades, llego ${JSON.stringify(amountMicros)}`,
    );
  }

  const micros = BigInt(amountMicros);
  const isNegative = micros < 0n;
  const magnitude = isNegative ? -micros : micros;

  const whole = magnitude / 1_000_000n;
  const fraction = (magnitude % 1_000_000n).toString().padStart(6, '0');

  return `${isNegative ? '-' : ''}${whole.toString()}.${fraction}`;
}

/**
 * Formatea un importe UNITARIO: el costo de un solo mensaje (RET-1694, DS-25).
 *
 * Existe porque `formatWalletAmount` redondea a centavos, y eso es correcto para
 * un saldo o una recarga pero destruye un precio por mensaje. Las tarifas de
 * Meta son fracciones de centavo —marketing en México cuesta US$0.0305 y
 * utility US$0.0085—, así que a dos decimales el primero se pinta "$0.03", el
 * segundo "$0.01", y un destino más barato **"$0.00"**: el mismo "gratis"
 * inventado que este ticket existe para evitar, ahora al lado de un botón de
 * enviar.
 *
 * `DS-25` (leadbilling/docs/business-rules/wallet/descuento-por-envio.md) fija
 * el contrato de PANTALLA — y sólo de pantalla: lo que se descuenta, la reserva
 * y el saldo siguen exactos (`microsToDecimal`, `formatWalletAmount`):
 *
 *   * **Cuatro decimales, siempre fijos** — nunca "significativos" — aunque
 *     los últimos sean cero (`$1.5000 USD`), para que la columna del selector
 *     alinee renglón contra renglón.
 *   * **Redondeo hacia ARRIBA** a la diezmilésima de unidad (100 micros),
 *     nunca al más cercano: la pantalla nunca muestra menos de lo que se
 *     descuenta (742980 micros, exactos $0.74298, se lee `$0.7430`), y ninguna
 *     tarifa mayor a cero se pinta `$0.0000` — eso se leería como gratis.
 *
 * El techo se calcula en BigInt — es una división de enteros no negativos,
 * `ceil(a / b) = floor((a + b - 1) / b)`, así que se resuelve sin dividir en
 * float. La conversión final a Number, para que Intl ponga símbolo y
 * separadores, es exacta para cualquier precio por mensaje real: se hace tras
 * comprobar que el resultado cae en el rango seguro — igual que
 * `formatWalletAmount`.
 */
export function formatWalletUnitAmount(options: {
  amountMicros: string;
  currency: tWalletCurrency;
  locale: string;
}): string {
  // Mismo guard que microsToDecimal: BigInt() lanza un SyntaxError crudo ante
  // cualquier cosa que no sea un entero ('', ' 5 ', '0x10', '1.5', 'abc'), y
  // eso se reportaría como un fallo de infraestructura en vez del input
  // inválido que es. Se rechaza con un Error que nombra la función y el valor.
  if (!/^-?\d+$/.test(options.amountMicros)) {
    throw new Error(
      `formatWalletUnitAmount: se esperaba un entero en micro-unidades, llego ${JSON.stringify(options.amountMicros)}`,
    );
  }

  const micros = BigInt(options.amountMicros);

  // Un precio por mensaje no es negativo — falla ruidoso en vez de pintar un
  // signo que no tiene sentido ahí.
  if (micros < 0n) {
    throw new RangeError(
      `wallet unit amount out of displayable range: ${options.amountMicros}`,
    );
  }

  // DS-25: cuatro decimales = diezmilésimas de unidad = 100 micros cada una.
  // Techo en BigInt puro, sin división en float.
  const tenThousandths =
    (micros + MICROS_PER_TEN_THOUSANDTH - 1n) / MICROS_PER_TEN_THOUSANDTH;

  // Mismo guard que formatWalletAmount: pasado el entero seguro la conversión
  // a Number empezaría a perder dígitos, y un precio silenciosamente
  // equivocado es peor que un fallo ruidoso.
  if (tenThousandths > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError(
      `wallet unit amount out of displayable range: ${options.amountMicros}`,
    );
  }

  const units = Number(tenThousandths) / 10_000;

  const formatted = walletAmountFormatter({
    currency: options.currency,
    locale: options.locale,
    fractionDigits: 4,
  }).format(units);

  return `${formatted}${CURRENCY_SUFFIX_SEPARATOR}${options.currency}`;
}

/** Una diezmilésima de unidad (DS-25), en micro-unidades (10⁶ / 10⁴ = 100). */
const MICROS_PER_TEN_THOUSANDTH = 100n;

/**
 * El costo que la barra «sin saldo» del compositor dice que no alcanza
 * (`DS-03`, leído como `DS-25` desde el 8-oct-2026, RET-2356).
 *
 * Es el precio de UN mensaje —el de servicio o el de la plantilla—, así que se
 * lee igual que la columna de la lista: cuatro decimales, hacia arriba. Con el
 * redondeo a centavos de `formatWalletAmount`, el servicio a Colombia
 * (US$0.0008) se leía «cuesta $0.00 USD y no te alcanza», y la barra de
 * plantilla daba otra cifra que la columna de al lado para el mismo envío.
 *
 * Las dos barras (texto libre y plantilla) pasan por aquí, para que no puedan
 * discrepar entre sí.
 *
 * NUNCA LANZA. `formatWalletUnitAmount` sí, ante un monto negativo, que como
 * precio no tiene sentido pero que la frontera del estimador (`/^-?\d+$/`)
 * deja pasar. En la barra de texto libre eso tumbaría el compositor, y en la
 * de plantilla caería al `catch` del guardia, que deja de bloquear. Ante eso
 * se pinta con el formateador de saldos, que sí sabe de signos: una cifra
 * rara es mejor que un compositor caído o un envío que el guardia dejó de
 * frenar por un problema de formato.
 */
export function formatComposerCostLabel(options: {
  amountMicros: string;
  currency: tWalletCurrency;
  locale: string;
}): string {
  try {
    return formatWalletUnitAmount(options);
  } catch {
    return formatWalletAmount(options);
  }
}

/**
 * Estrecha a moneda soportada el `currency` ABIERTO que devuelve el estimador.
 *
 * `tSendCostEstimate.currency` es `string` a propósito (RET-1806): es el
 * contrato del estimador, no el del saldo, y cerrarlo haría que el tipo mienta
 * el día que el catálogo devuelva una moneda que el front todavía no formatea.
 * La contrapartida es que quien va a FORMATEAR tiene que validarlo antes, y eso
 * es exactamente lo que hace esto.
 *
 * Devuelve `''` ante una moneda desconocida, que es el valor que
 * `resolveWalletCurrency` interpreta como "no la sé, usa el respaldo" — nunca un
 * default inventado, porque pintar un importe con el símbolo de otra moneda es
 * peor que no pintarlo.
 */
export function asWalletCurrency(currency: string): tWalletCurrencyOrEmpty {
  return WALLET_CURRENCIES.includes(currency as tWalletCurrency)
    ? (currency as tWalletCurrency)
    : '';
}

/**
 * Escala un monto en micros por un número entero de veces.
 *
 * Existe para el saldo incluido de los planes no mensuales (RET-1961, regla
 * `SA-08`): el catálogo guarda el monto POR MES, y quien compra trimestral no
 * lo recibe cada mes sino una vez por ciclo, por los tres juntos. La pantalla
 * tiene que decir lo que de verdad se entrega.
 *
 * En BigInt y no en `Number`: multiplicar micros es exactamente donde un float
 * empieza a perder centavos, y aquí el resultado se le muestra al cliente.
 * Falla cerrado ante un monto ilegible o un factor que no sea un entero
 * positivo — sobre lo que no se entiende no se hace una promesa.
 */
export function multiplyMicros(
  amountMicros: string,
  factor: number,
): string | null {
  if (!Number.isInteger(factor) || factor < 1) return null;

  try {
    return (BigInt(amountMicros) * BigInt(factor)).toString();
  } catch {
    return null;
  }
}

/**
 * ¿Este monto en micros es una cantidad que valga la pena afirmarle al cliente?
 *
 * Existe porque `Boolean(micros)` no alcanza: `"0"` es un string con contenido y
 * pasa la puerta, así que un catálogo que responda cero termina prometiendo
 * "$0.00 de saldo incluido cada mes" — el gratis inventado que este archivo
 * existe para evitar, sólo que con la cifra escrita.
 *
 * Falla cerrado ante basura: un monto que `BigInt` no puede leer no es un cero,
 * es una respuesta que no entendemos, y sobre eso no se hace una promesa.
 */
export function hasPositiveMicros(amountMicros: string | null | undefined): boolean {
  if (!amountMicros) return false;

  try {
    return BigInt(amountMicros) > 0n;
  } catch {
    return false;
  }
}

/**
 * ¿Este saldo en micros es un cero o un negativo que SÍ pudimos leer?
 * (RET-2348)
 *
 * NO es `!hasPositiveMicros(...)`, y la diferencia es todo el punto. Aquella
 * contesta `false` también ante lo que no entiende, que es lo correcto para
 * una promesa («no afirmes un monto que no leíste»). Negarla convertiría «no
 * sé cuánto hay» en «no hay saldo», y con eso se le avisa a un cliente que
 * hoy no puede enviar sin que nadie lo sepa.
 *
 * Por eso valida con una expresión y no con `BigInt` en un try/catch:
 * `BigInt('')` devuelve `0n` SIN lanzar, así que un campo que no llegó pasaría
 * como un saldo en cero perfectamente legible. Mismo criterio que
 * `isReadableMicros` en `Wallet.service.ts`.
 */
export function isZeroOrNegativeMicros(
  amountMicros: string | null | undefined,
): boolean {
  if (!amountMicros || !/^-?\d+$/.test(amountMicros)) return false;

  return BigInt(amountMicros) <= 0n;
}
